import { inject, Injectable } from '@angular/core';
import { AppConfigService } from './app-config.service';

export const INDEXED_DB_KEYS = {
  firmIdList: 'firmIdList',
  firmsToValidate: 'firms-to-validate',
} as const;

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  private appConfig = inject(AppConfigService);

  private readonly dbVersion = 1;
  private readonly storeName = 'keyval';
  private dbPromise: Promise<IDBDatabase> | null = null;

  private get dbName(): string {
    return `${this.appConfig.dbName}_indexeddb`;
  }

  private ensureBrowserApi(): void {
    if (typeof window === 'undefined' || !window.indexedDB) {
      throw new Error('IndexedDB is not available in this environment.');
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    this.ensureBrowserApi();

    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(this.storeName)) {
          database.createObjectStore(this.storeName);
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        this.dbPromise = null;
        reject(request.error);
      };
    });

    return this.dbPromise;
  }

  private async withStore<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const database = await this.openDatabase();

    return new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(this.storeName, mode);
      const store = transaction.objectStore(this.storeName);
      const request = operation(store);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  getItem<T>(key: string): Promise<T | undefined> {
    return this.withStore<T | undefined>('readonly', store => store.get(key));
  }

  setItem<T>(key: string, value: T): Promise<IDBValidKey> {
    return this.withStore<IDBValidKey>('readwrite', store => store.put(value, key));
  }

  removeItem(key: string): Promise<undefined> {
    return this.withStore<undefined>('readwrite', store => store.delete(key));
  }

  clear(): Promise<undefined> {
    return this.withStore<undefined>('readwrite', store => store.clear());
  }

  keys(): Promise<IDBValidKey[]> {
    return this.withStore<IDBValidKey[]>('readonly', store => store.getAllKeys());
  }

  close(): void {
    if (!this.dbPromise) {
      return;
    }

    this.dbPromise
      .then(database => database.close())
      .finally(() => {
        this.dbPromise = null;
      });
  }
}